import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getBuddingSensitivity,
  getCellSizeOptions,
  getStainOptions,
  getSugarUnitOptions,
} from '@/features/user-settings';

import { IBatch, Method, WineMethods, YeastMethods } from '../../types';
import { getAnalysesMethodDescription } from '../../utils/get-analyses-method-desc';

interface IProps {
  batch: IBatch;
}

export const AnalysisInfo: React.FC<IProps> = ({ batch }) => {
  const { t } = useTranslation();

  const stainOptions = useMemo(() => getStainOptions(), []);
  const stainMethod =
    stainOptions.find((stain) => stain.value === batch.methodAttributes.stain)?.label ||
    '';

  const cellSizeOptions = useMemo(() => getCellSizeOptions(), []);
  const cellSize =
    cellSizeOptions.find((size) => size.value === batch.methodAttributes.analysisLevel)
      ?.label || '';

  const sugarUnitOptions = useMemo(() => getSugarUnitOptions(), []);
  const sugarUnit =
    sugarUnitOptions.find((unit) => unit.value === batch.scaleType)?.label || '';

  const buddingSensitivityOptions = useMemo(() => getBuddingSensitivity(), []);
  const sensitivity =
    buddingSensitivityOptions.find(
      (option) => option.value === batch.tags.budding_sensitivity,
    )?.label || '';

  const formatDateTime = (date: Date) => {
    return `${date.toLocaleDateString('en-CA')} at ${date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })}`;
  };

  const created = useMemo(() => {
    const date = batch.locallyCreatedAt || batch.createdAt;
    return formatDateTime(new Date(date));
  }, [batch.createdAt, batch.fermentedAt]);

  return (
    <div className="flex flex-col gap-8">
      <h2 className="font-semibold text-gray-600">Details</h2>
      <hr />

      <InfoItem title="Created">
        <>{created}</>
      </InfoItem>

      <InfoItem title="measurements.details.user">{batch.email}</InfoItem>

      <InfoItem title="measurements.details.analysisMethod">
        {t(getAnalysesMethodDescription(batch.method))}
      </InfoItem>

      <InfoItem title="measurements.details.dilution">
        <>
          {batch.dilutionSample1 ?? 0} : {batch.dilutionSample2 ?? 0}
        </>
      </InfoItem>

      {[Method.WineConzViab, Method.Yeast2ConzViab, Method.BioEthConzViab].includes(
        batch.method,
      ) ? (
        <InfoItem title="measurements.details.colorSolutions">
          <>
            {batch.dilutionStain1 ?? 0} : {batch.dilutionStain2 ?? 0}
          </>
        </InfoItem>
      ) : null}

      {batch.density ? (
        <InfoItem title="measurements.details.sugarContent">{batch.density}</InfoItem>
      ) : null}

      {batch.temperature ? (
        <InfoItem title="measurements.details.temperature">{batch.temperature}</InfoItem>
      ) : null}

      {batch.ph ? <InfoItem title="measurements.details.ph">{batch.ph}</InfoItem> : null}

      {batch.turbidity ? (
        <InfoItem title="measurements.details.turbidity">{batch.turbidity}</InfoItem>
      ) : null}

      {batch.description ? (
        <InfoItem title="measurements.details.comment">{batch.description}</InfoItem>
      ) : null}

      {stainMethod ? (
        <InfoItem title="measurements.details.stain">{t(`${stainMethod}`)}</InfoItem>
      ) : null}

      {cellSize ? (
        <InfoItem title="userSettings.cellSize">{t(`${cellSize}`)}</InfoItem>
      ) : null}

      {[
        Method.WineConzViab,
        Method.WineConz,
        Method.WineBrett,
        Method.WineManualCnt,
      ].includes(batch.method) && sugarUnit ? (
        <InfoItem title="userSettings.sugarUnit">{t(`${sugarUnit}`)}</InfoItem>
      ) : null}

      {[Method.Yeast2Conz, Method.Yeast2ConzViab, Method.Yeast2Brett].includes(
        batch.method,
      ) && batch.tags !== undefined ? (
        <InfoItem title="measurements.details.elongatedCell">
          {batch.tags.long_cells ? t('common.yes') : t('common.no')}
        </InfoItem>
      ) : null}

      {[...YeastMethods, ...WineMethods].includes(batch.method) &&
      batch.tags !== undefined ? (
        <InfoItem title="measurements.details.lactobacillusDetection">
          {batch.tags.detect_lact ? t('common.yes') : t('common.no')}
        </InfoItem>
      ) : null}

      {[Method.BioEthConz, Method.BioEthConzViab, Method.BioEthrett].includes(
        batch.method,
      ) && batch.tags !== undefined ? (
        <InfoItem title="userSettings.ethanolBuddingSensitivity">
          {batch.tags.budding_sensitivity ? t(`${sensitivity}`) : t('common.no')}
        </InfoItem>
      ) : null}

      {batch.id ? (
        <InfoItem title="Analyses ID">{batch.id.toUpperCase()}</InfoItem>
      ) : null}
      <hr />
    </div>
  );
};

const InfoItem = ({
  title,
  children,
}: {
  title: string;
  children: string | number | JSX.Element;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex gap-2">
      <span className="flex-1 text-gray-600">{t(title)}</span>
      <span className="flex-1 text-gray-600">{children}</span>
    </div>
  );
};
